import type { FormFieldSchema } from "../services/api";

/**
 * Build the initial form-data map from each field's configured default, so a
 * choice field can open with an option already selected (issue #30). Only
 * option-backed choice fields carry a default; everything else starts empty.
 * Single-choice defaults are a string; multi-choice defaults are a string[].
 */
export function initialFormValues(
  fields: FormFieldSchema[],
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of fields) {
    const dv = f.default_value;
    if (dv === undefined || dv === null) continue;
    if (f.type === "multi_select" || f.type === "checkbox_group") {
      if (Array.isArray(dv) && dv.length > 0) out[f.id] = [...dv];
    } else if (f.type === "single_select" || f.type === "radio_group") {
      if (typeof dv === "string" && dv !== "") out[f.id] = dv;
    }
  }
  return out;
}

/**
 * Render a submitted field value for the Review screen and the request detail
 * slide-over. Returns null when the value is effectively empty so callers can
 * skip the row. Option-backed fields resolve to their human label.
 */
export function formatFieldValue(field: FormFieldSchema, value: unknown): string | null {
  if (
    value === undefined ||
    value === null ||
    value === "" ||
    (Array.isArray(value) && value.length === 0)
  ) {
    return null;
  }

  const labelFor = (v: unknown) =>
    field.options?.find((o) => o.value === v)?.label ?? String(v);

  switch (field.type) {
    case "single_checkbox":
      return value ? "Yes" : "No";
    case "checkbox_group":
    case "multi_select":
      return (value as unknown[]).map(labelFor).join(", ");
    case "single_select":
    case "radio_group":
      return labelFor(value);
    case "file_upload":
      return (value as string[])
        .map((u) => decodeURIComponent(u.split("/").pop() ?? u))
        .join(", ");
    case "number":
      return field.is_currency ? `$${value}` : String(value);
    default:
      return String(value);
  }
}
